import { iZettleMarket } from "@izettle/constants"
import type { BackOfficeSetupProps } from "@izettle/react-spa"
// import { envsafe, url } from 'envsafe'

const errors: string[] = []

const envVar = <T extends string, D extends T | undefined = T>(
  value: string | undefined,
  defaultValue?: D,
): D extends undefined ? T | undefined : T => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return (value || defaultValue) as any
}

const requiredEnvVar = <T extends string = string>(
  value: string | undefined,
  key: string,
) => {
  if (value == null) {
    errors.push(`Env variable \`${key}\` is required but was not found.`)
  }

  return value as T
}

const overrideWithEnvVar = (value: string, overrideEnvVar: string | undefined) => {
  // We have to use || since we want to override empty strings.
  // eslint-disable-next-line
  return overrideEnvVar || value
}

export enum Environment {
  Production = "production",
  Staging = "staging",
  Test = "test",
  Development = "development",
}

export type AppEnvironment =
  | Environment.Development
  | Environment.Staging
  | Environment.Production
export type NodeEnvironment =
  | Environment.Development
  | Environment.Test
  | Environment.Production

type Config = BackOfficeSetupProps["config"] & {
  appEnv: AppEnvironment
  nodeEnv: NodeEnvironment
  url: string
  /** The path the app will be presented on */
  publicPath: string

  /** The Client header to set when calling keyval-service ([Name]-[Version]) */
  client: string

  checkout: {
    url: string
  }

  login: {
    url: string
  }

  purchase: {
    url: string
    urlUS: string
    getUrl: (merchantMarket: iZettleMarket) => string
  }

  oauth: {
    url: string
    zClientId: string
  }

  purchaseSearch: {
    url: string
  }

  reports: {
    url: string
  }

  sun: {
    url: string
  }

  sites: {
    url: string
  }
}

const appEnv = envVar<Config["appEnv"]>(process.env.APP_ENV, Environment.Staging)
const nodeEnv = envVar<Config["nodeEnv"]>(process.env.NODE_ENV, Environment.Development)

// const getServiceUrl = (subdomain: string) =>
//   `https://${subdomain}.${process.env.APP_ENV === 'production' ? 'izettle' : 'izettletest'}.com`;

// NOTE: `process.env.WHATEVER` HAS TO BE FULLY WRITTEN EACH TIME.
// Webpack's EnvironmentPlugin cannot replace usages of process.env[variable]
// with the correct values so we have to write it out fully each time.
// const env = envsafe({
//   IZ_APP_PURCHASES_URL: url({
//     default: getServiceUrl('purchase'),
//     input: process.env.IZ_APP_RECEIPTS_PATH
//   })
// })

const getServiceUrl = (subdomain: string) =>
  `https://${subdomain}.${appEnv === "production" ? "izettle" : "izettletest"}.com`

const getNewServiceUrl = (subdomain: string) =>
  `https://${subdomain}.${appEnv === "production" ? "zettle" : "zettletest"}.com`

export const config: Config = {
  appEnv,
  nodeEnv,
  url: getServiceUrl("my"),
  publicPath: envVar(process.env.IZ_APP_PUBLIC_PATH, "/transactions-v2"),

  client: `ReceiptsWeb-${process.env.GIT_SHA?.slice(0, 7)}`,

  oauth: {
    url: overrideWithEnvVar(getNewServiceUrl("oauth"), process.env.IZ_APP_OAUTH_URL),
    clientId: requiredEnvVar(
      process.env.IZ_APP_OAUTH_CLIENT_ID,
      "IZ_APP_OAUTH_CLIENT_ID",
    ),
    zClientId: requiredEnvVar(
      process.env.IZ_APP_ZETTLE_OAUTH_CLIENT_ID,
      "IZ_APP_ZETTLE_OAUTH_CLIENT_ID",
    ),
  },

  login: {
    url: overrideWithEnvVar(getNewServiceUrl("login"), process.env.IZ_APP_LOGIN_URL),
  },
  checkout: {
    url: overrideWithEnvVar(getServiceUrl("checkout"), process.env.IZ_APP_CHECKOUT_URL),
  },
  purchase: {
    url: overrideWithEnvVar(getServiceUrl("purchase"), process.env.IZ_APP_PURCHASE_URL),
    urlUS: overrideWithEnvVar(
      getServiceUrl("purchase-us"),
      process.env.IZ_APP_PURCHASE_US_URL,
    ),
    getUrl(merchantMarket: iZettleMarket) {
      if (merchantMarket === iZettleMarket.US) {
        return this.urlUS
      }
      return this.url
    },
  },
  purchaseSearch: {
    url: overrideWithEnvVar(
      getNewServiceUrl("purchase"),
      process.env.IZ_APP_PURCHASE_SEARCH_URL,
    ),
  },
  reports: {
    url: overrideWithEnvVar(getServiceUrl("reports"), process.env.IZ_APP_REPORTS_URL),
  },

  sun: {
    url: overrideWithEnvVar(getServiceUrl("www"), process.env.IZ_APP_SUN_URL),
  },

  sites: {
    url: overrideWithEnvVar(getServiceUrl("sites"), process.env.IZ_APP_SUN_URL),
  },
}

if (config.nodeEnv !== "test" && errors.length > 0) {
  throw new Error(`Invalid config!\n  • ${errors.join("\n  • ")}`)
}
